// ABOUTME: Admin knowledge base management interface with tabbed navigation
// ABOUTME: Provides access to article, video, category, and tag management

import React, { useState } from 'react';
import { Box, Tabs, Tab, Container, Typography, Button } from '@mui/material';
import { ArrowBack, Article, VideoLibrary, Category, LocalOffer } from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import ArticleManager from './kb/ArticleManager';
import VideoManager from './kb/VideoManager';
import CategoryManager from './kb/CategoryManager';
import TagManager from './kb/TagManager';
import ErrorBoundary from '../ErrorBoundary';

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

const TabPanel: React.FC<TabPanelProps> = ({ children, value, index }) => {
  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`kb-tabpanel-${index}`}
      aria-labelledby={`kb-tab-${index}`}
    >
      {value === index && <Box sx={{ py: 3 }}>{children}</Box>}
    </div>
  );
};

const KnowledgeBaseTab: React.FC = () => {
  const navigate = useNavigate();
  const [currentTab, setCurrentTab] = useState(0);

  const handleTabChange = (_event: React.SyntheticEvent, newValue: number) => {
    setCurrentTab(newValue);
  };

  const handleNavigateBack = () => {
    navigate('/admin');
  };

  return (
    <Container maxWidth="xl" sx={{ py: 4 }}>
      {/* Header */}
      <Box sx={{ mb: 4, display: 'flex', alignItems: 'center', gap: 2 }}>
        <Button
          variant="outlined"
          startIcon={<ArrowBack />}
          onClick={handleNavigateBack}
          sx={{ mr: 2 }}
        >
          Back to Admin
        </Button>
        <Box>
          <Typography variant="h3" component="h1" sx={{ fontWeight: 600, mb: 1 }}>
            Knowledge Base Management
          </Typography>
          <Typography variant="body1" color="text.secondary">
            Manage articles, videos, categories, and tags
          </Typography>
        </Box>
      </Box>

      {/* Tabs Navigation */}
      <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 3 }}>
        <Tabs
          value={currentTab}
          onChange={handleTabChange}
          aria-label="knowledge base management tabs"
        >
          <Tab
            icon={<Article />}
            iconPosition="start"
            label="Articles"
            id="kb-tab-0"
            aria-controls="kb-tabpanel-0"
          />
          <Tab
            icon={<VideoLibrary />}
            iconPosition="start"
            label="Videos"
            id="kb-tab-1"
            aria-controls="kb-tabpanel-1"
          />
          <Tab
            icon={<Category />}
            iconPosition="start"
            label="Categories"
            id="kb-tab-2"
            aria-controls="kb-tabpanel-2"
          />
          <Tab
            icon={<LocalOffer />}
            iconPosition="start"
            label="Tags"
            id="kb-tab-3"
            aria-controls="kb-tabpanel-3"
          />
        </Tabs>
      </Box>

      {/* Tab Panels */}
      <TabPanel value={currentTab} index={0}>
        <ErrorBoundary>
          <ArticleManager />
        </ErrorBoundary>
      </TabPanel>

      <TabPanel value={currentTab} index={1}>
        <ErrorBoundary>
          <VideoManager />
        </ErrorBoundary>
      </TabPanel>

      <TabPanel value={currentTab} index={2}>
        <ErrorBoundary>
          <CategoryManager />
        </ErrorBoundary>
      </TabPanel>

      <TabPanel value={currentTab} index={3}>
        <ErrorBoundary>
          <TagManager />
        </ErrorBoundary>
      </TabPanel>
    </Container>
  );
};

export default KnowledgeBaseTab;
